import { useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Eye, EyeOff, Mail } from "lucide-react";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { passwordStrength } from "@/utils/passwordStrength";
import { API } from "@/App";

/**
 * Login / signup page.
 *
 * Layout (desktop, ≥ lg):
 *   ┌────────────────────────────────────┬────────────────────────┐
 *   │ Wokelo wordmark (top-left, pt-7)   │                        │
 *   │                                    │   ┌────────────────┐   │
 *   │       Slides by Wokelo             │   │  Hello there!  │   │
 *   │   AI for consulting-grade PPTs     │   │  …auth card…   │   │
 *   │                                    │   └────────────────┘   │
 *   │       [4×3 gallery grid]           │                        │
 *   │                                    │                        │
 *   │  Trusted by elite consultants…     │                        │
 *   │  KPMG  Deloitte  Bain  …           │                        │
 *   └────────────────────────────────────┴────────────────────────┘
 *
 * Pixel-perfect targets are derived from V1.png (1440×922):
 *   - LHS panel width:    870px (60%)
 *   - Auth card width:    470px, height clamps to viewport
 *   - Wordmark top inset: 28px
 *   - Heading font:       Figtree (loaded in index.css), 44px, ExtraBold
 *
 * Mobile (< lg):
 *   - LHS panel hidden; the auth card centers on the whole viewport with
 *     a small Wokelo wordmark above it.
 *
 * Functional contract (unchanged):
 *   - Email/password login → redirects to /generator
 *   - Email/password signup → 2-stage email-verification inbox screen
 *   - Google OAuth → /auth/google/start (referral code propagated)
 *   - Forgot-password modal
 */
export default function LoginPage() {
  const location = useLocation();
  const initialMode = location.pathname === "/signup" ? "signup" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resending, setResending] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const referralCode = new URLSearchParams(window.location.search).get("ref") || undefined;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "signup") {
      if (!name || !email || !password) {
        toast.error("Please fill in all fields");
        return;
      }
      const s = passwordStrength(password);
      if (!s.meets) {
        toast.error(s.hint || "Password is too weak");
        return;
      }
    } else if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        const result = await register(email, password, name, referralCode);
        if (result?.verification_sent) {
          setPendingEmail(email);
          toast.success("Verification email sent");
          return;
        }
        toast.success("Welcome to Slides by Wokelo!");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
      navigate("/generator");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      // The global axios interceptor (lib/api.js) silently retries
      // 502/503/504 + Cloudflare 520-530 + network errors for ~63s.
      // If we reach this catch block, EITHER:
      //   (a) the backend returned a deterministic auth error (401, 403, 422)
      //       — show the backend's detail message
      //   (b) the silent retries were exhausted (real outage, not cold-start)
      //       — show the warming-up message as a soft fallback
      //   (c) the request was cancelled / browser blocked it (no response)
      let message;
      const isInfraStatus =
        status === 502 || status === 503 || status === 504 ||
        (status >= 520 && status <= 530);
      if (!error.response) {
        message = "Couldn't reach the server. Please try again in a moment.";
      } else if (isInfraStatus || status >= 500) {
        message = "Server is taking longer than usual to wake up — please try again in a moment.";
      } else if (typeof detail === "string") {
        // Backend gave us a specific message (e.g. "Invalid credentials",
        // "Account deactivated", or signup validation errors).
        message = detail;
      } else if (mode === "signup") {
        message = "Unable to create account";
      } else {
        message = "Invalid email or password";
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    setGoogleLoading(true);
    const params = new URLSearchParams({ next: "/generator" });
    if (referralCode) params.set("ref", referralCode);
    window.location.href = `${API}/auth/google/start?${params.toString()}`;
  };

  const isFormValid = mode === "signup" ? (name && email && password) : (email && password);

  const handleResendVerification = async () => {
    if (!pendingEmail) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: pendingEmail });
      toast.success("New verification link sent");
    } catch {
      toast.error("Couldn't resend right now. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  // ── Post-signup inbox screen (unchanged) ─────────────────────────
  if (pendingEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4] px-4 font-['Figtree']" data-testid="signup-pending-page">
        <Card className="w-full max-w-md border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-teal-50 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-teal-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Confirm your email</h1>
            <p className="text-sm text-gray-600 leading-relaxed">
              We sent a confirmation link to{" "}
              <strong className="text-gray-900">{pendingEmail}</strong>.
              Click it to activate your account and grab your 50 free trial credits.
            </p>
            <p className="text-xs text-gray-400 mt-4">
              The link expires in 24 hours. Check your spam folder if it doesn't arrive in a couple of minutes.
            </p>
            <div className="mt-6 space-y-2">
              <Button
                onClick={handleResendVerification}
                disabled={resending}
                variant="outline"
                className="w-full"
                data-testid="resend-verification-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend email"}
              </Button>
              <Button
                onClick={() => { setPendingEmail(""); setMode("login"); }}
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-900"
                data-testid="back-to-login"
              >
                Use a different email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full flex bg-[#f4f4f4] overflow-hidden font-['Figtree']"
      data-testid="login-page"
    >
      {/* ── LEFT: Marketing panel (desktop only, ≥ lg) ─────────────── */}
      {/* The RHS auth card is vertically centered with a content height
          of roughly 610px (px-10 py-14 box + form). To keep the LHS
          content visually aligned to the card's vertical range AT ANY
          viewport height we anchor the LHS padding to the card's edges:

            card_top    = (vh - 610) / 2
            card_bottom = (vh + 610) / 2

          We add a small 10px inset on each side so the LHS wordmark sits
          *just below* the card's top edge and the logo strip sits *just
          above* the card's bottom edge — matches the latest design
          feedback (Feb 2026). */}
      <div className="hidden lg:flex flex-col lg:w-[58%] xl:w-[60%] px-12 pt-[calc(50vh-333px)] pb-[calc(50vh-257px)] relative">
        {/* Wokelo wordmark — pinned to the top-left CORNER of the panel,
            independent of the centered LHS content stack below. This matches
            the original V1 mockup where the logo sits at the absolute top-
            left of the viewport (not aligned with the auth card). */}
        <img
          src="/landing/wokelo_logo.svg"
          alt="Wokelo"
          className="absolute top-8 left-12 w-[110px] h-auto select-none"
          data-testid="landing-wokelo-logo"
          draggable={false}
        />

        {/* Centered content block. `min-h-0` lets the gallery image shrink
            to fit the remaining flex space (preventing overflow on short
            viewports). */}
        <div className="flex-1 flex flex-col items-center justify-center min-h-0 py-4">
          <p className="text-[14px] text-gray-500 font-normal mb-2">
            Slides by Wokelo
          </p>
          <h1
            className="text-[31px] xl:text-[36px] leading-[1.05] font-extrabold text-gray-900 tracking-tight text-center mb-7"
            data-testid="landing-headline"
          >
            AI for consulting-grade PPTs
          </h1>
          {/* Gallery — single SVG with 4×3 = 12 PPT thumbnails. Capped by
              `max-h-[44vh]` so the row never pushes the trusted-by strip
              off-screen on shorter laptops; aspect ratio preserved by the
              SVG's intrinsic viewBox. */}
          <img
            src="/landing/gallery-grid.svg"
            alt="Sample slides"
            className="w-full max-w-[680px] max-h-[36vh] h-auto object-contain select-none"
            data-testid="landing-gallery"
            draggable={false}
          />
        </div>

        {/* Trusted-by footer — sits at the bottom of the clamped LHS
            column. With `pb-[14vh]` on the parent the logo strip ends
            just above the auth card's bottom edge on a 1440×922 viewport. */}
        <div className="text-center">
          <p className="text-[14px] text-gray-400 mb-5 tracking-wide">
            Trusted by elite consulting and deal teams
          </p>
          {/* Logo strip: KPMG · Deloitte · Bain · McKinsey · BCG · A&M
              (the V1 order). Tighter horizontal spacing per latest
              feedback so the strip reads as a single confident row. */}
          <div className="flex items-center justify-center gap-x-5 xl:gap-x-7 flex-wrap gap-y-3">
            <img src="/landing/logo-kpmg.svg"     alt="KPMG"        className="h-9 select-none" draggable={false} />
            <img src="/landing/logo-deloitte.svg" alt="Deloitte"    className="h-[34px] select-none" draggable={false} />
            <img src="/landing/logo-bain.svg"     alt="Bain & Company" className="h-[26px] select-none" draggable={false} />
            <img src="/landing/logo-mckinsey.svg" alt="McKinsey & Company" className="h-9 select-none" draggable={false} />
            <img src="/landing/logo-bcg.svg"      alt="BCG"         className="h-[29px] select-none" draggable={false} />
            <img src="/landing/logo-am.svg"       alt="Alvarez & Marsal" className="h-[42px] select-none" draggable={false} />
          </div>
        </div>
      </div>

      {/* ── RIGHT: Auth card ────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-6 overflow-y-auto">
        <div
          className="w-full max-w-[470px] bg-white rounded-[28px] shadow-[0_10px_40px_rgb(0,0,0,0.05)] border border-gray-100 px-10 py-14"
          data-testid="auth-card"
        >
          {/* Brand mark on mobile only — desktop has the wordmark on the
              marketing panel */}
          <div className="lg:hidden text-center mb-5">
            <img src="/landing/wokelo_logo.svg" alt="Wokelo" className="h-7 mx-auto" />
          </div>

          <div className="text-center mb-6">
            <h2
              className="text-[28px] leading-tight font-bold text-gray-900 tracking-tight"
              data-testid="auth-heading"
            >
              {mode === "signup" ? "Create your account" : "Hello there!"}
            </h2>
            <p className="text-[14px] text-gray-500 mt-1.5">
              {mode === "signup"
                ? "Start with 50 free trial credits"
                : "Enter password to get started"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
            {mode === "signup" && (
              <Input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 bg-gray-50 border border-gray-100 rounded-xl px-4 text-[14px] placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-gray-900 focus-visible:ring-offset-0 focus-visible:bg-white transition-all"
                data-testid="name-input"
                autoComplete="name"
              />
            )}
            <Input
              type="email"
              placeholder="josh@acme.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 bg-gray-50 border border-gray-100 rounded-xl px-4 text-[14px] placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-gray-900 focus-visible:ring-offset-0 focus-visible:bg-white transition-all"
              data-testid="email-input"
              autoComplete="email"
            />
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder={mode === "signup" ? "Password (min 8 chars, letters + numbers)" : "Password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-white border border-gray-200 rounded-xl px-4 pr-11 text-[14px] placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-gray-900 focus-visible:ring-offset-0 transition-all"
                data-testid="password-input"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="toggle-password-visibility"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {mode === "signup" && password && (
              <PasswordStrengthMeter value={password} testid="signup-pwd-strength" />
            )}

            <Button
              type="submit"
              className={`w-full h-12 rounded-xl font-semibold text-[14px] mt-3 transition-all ${
                isFormValid
                  ? "bg-gray-900 hover:bg-black text-white shadow-sm hover:shadow-md"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
              }`}
              disabled={loading || !isFormValid}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "signup" ? (
                "Create account"
              ) : (
                "Log in"
              )}
            </Button>

            {mode === "login" && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-[14px] text-teal-600 hover:text-teal-700 font-semibold transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>

          {/* "or" divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-[12px] text-gray-400">or</span>
            </div>
          </div>

          <div className="space-y-2.5">
            {/* Login with Google */}
            <Button
              type="button"
              onClick={handleGoogleAuth}
              disabled={googleLoading || loading}
              variant="outline"
              className="w-full h-12 rounded-xl border border-gray-200 bg-white hover:!bg-gray-50 text-gray-800 hover:!text-gray-900 font-medium text-[14px] transition-all"
              data-testid="google-auth-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <svg className="w-[18px] h-[18px] mr-2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Login with Google
                </>
              )}
            </Button>
          </div>

          {/* Terms */}
          <p className="text-[11px] text-center text-gray-400 mt-5 leading-relaxed">
            By {mode === "signup" ? "creating an account" : "signing in"}, I agree to Wokelo's{" "}
            <a href="https://www.wokelo.ai/legal?tab=privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline underline-offset-2 hover:text-gray-700">Privacy</a>
            {" "}and{" "}
            <a href="https://www.wokelo.ai/legal" target="_blank" rel="noopener noreferrer" className="text-gray-500 underline underline-offset-2 hover:text-gray-700">T&C</a>
          </p>

          {/* Bottom CTA — back to simple "Don't have an account? Sign up"
              in teal. The previous "Request access" variant was removed
              per the 2026-02 design feedback. */}
          <div className="text-center mt-5 pt-5 border-t border-gray-100">
            {mode === "login" ? (
              <>
                <p className="text-[14px] text-gray-500">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                    data-testid="switch-to-signup"
                  >
                    Sign up
                  </button>
                </p>
                <p className="text-[12px] text-gray-400 mt-1.5" data-testid="trial-credits-tagline">
                  Start with 50 free trial credits
                </p>
              </>
            ) : (
              <p className="text-[14px] text-gray-500">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                  data-testid="switch-to-login"
                >
                  Log in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
    </div>
  );
}
